import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback
} from 'react';
import ReactDOM from 'react-dom';
import { authService } from '../services/auth';
import {
  SUPPORTED_TEMPLATE_VARIABLES,
  SUPPORTED_TEMPLATE_FUNCTIONS
} from '../../core/platform/promptTemplates';

export interface PromptEditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

interface PromptEditorProps {
  value: string;
  onChange: (val: string) => void;
  variables?: Record<string, string>;
  agentName?: string;
  placeholder?: string;
  rows?: number;
}

interface TokenHoverData {
  token: string;
  type: 'variable' | 'function' | 'unknown';
  name?: string;
  argument?: string;
  targetUrl?: string | null;
  value?: string;
  source?: string;
  description?: string;
  status?: 'success' | 'warning' | 'info' | 'error';
  loading: boolean;
}

interface TooltipState {
  visible: boolean;
  top: number;
  left: number;
  placement: 'bottom' | 'top';
  align: 'left' | 'right';
  token: string;
}

// Регулярное выражение для токенизации текста:
// 1. Функции: {{curl(...)}} или {curl(...)}
// 2. Переменные: {{variable_name}} или {variable_name}
// 3. Секции/Заголовки: [ЗАГОЛОВОК]
// 4. Шаги/Пункты: Шаг 1:, 1., 2.
const MASTER_TOKEN_REGEX = /(\{\{\s*curl\s*\([^)]+?\)\s*\}\}|\{curl\s*\([^)]+?\)\})|(\{\{\s*[a-zA-Z0-9_-]+\s*\}\}|\{[a-zA-Z0-9_-]+\})|(\[[^\]\n]+\])|(^(?:Шаг\s+\d+|[0-9]+\.)[^\n:]*:)/gim;

export const PromptEditor = forwardRef<PromptEditorHandle, PromptEditorProps>(({
  value = '',
  onChange,
  variables = {},
  agentName = 'Алексей',
  placeholder = 'Введите системный промпт с переменными и вызовами функций...',
  rows = 10
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLPreElement>(null);
  const cursorPosRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  // Кэш для результатов XHR запросов к /api/prompt/evaluate-token
  const evalCache = useRef<Record<string, TokenHoverData>>({});
  
  // Состояние тултипа
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    visible: false,
    top: 0,
    left: 0,
    placement: 'bottom',
    align: 'left',
    token: ''
  });
  const [hoverData, setHoverData] = useState<TokenHoverData | null>(null);

  const activeTokenRef = useRef<string | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOverTooltipRef = useRef<boolean>(false);

  // Синхронизация скролла между textarea и backdrop
  const syncScroll = useCallback(() => {
    if (textareaRef.current && backdropRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  // Сохранение позиции курсора
  const handleUpdateCursor = useCallback(() => {
    if (textareaRef.current) {
      cursorPosRef.current = {
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd
      };
    }
  }, []);

  // Вставка текста в текущую позицию курсора
  const insertAtCursor = useCallback((textToInsert: string) => {
    const textarea = textareaRef.current;
    const currentVal = value || '';
    
    const start = textarea ? textarea.selectionStart : cursorPosRef.current.start;
    const end = textarea ? textarea.selectionEnd : cursorPosRef.current.end;

    const before = currentVal.substring(0, start);
    const after = currentVal.substring(end);
    const nextVal = before + textToInsert + after;

    onChange(nextVal);

    const nextPos = start + textToInsert.length;
    cursorPosRef.current = { start: nextPos, end: nextPos };

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(nextPos, nextPos);
        syncScroll();
      }
    });
  }, [value, onChange, syncScroll]);

  useImperativeHandle(ref, () => ({
    insertAtCursor,
    focus: () => {
      textareaRef.current?.focus();
    }
  }), [insertAtCursor]);

  // Закрытие тултипа
  const closeTooltip = useCallback(() => {
    if (isOverTooltipRef.current) return;
    activeTokenRef.current = null;
    setTooltipState(prev => ({ ...prev, visible: false }));
    setHoverData(null);
  }, []);

  // Запрос значения токена через XHR
  const fetchTokenEvaluation = useCallback(async (token: string) => {
    // Если уже есть в кэше — сразу отдаем
    if (evalCache.current[token] && !evalCache.current[token].loading) {
      setHoverData(evalCache.current[token]);
      return;
    }

    // Устанавливаем статус загрузки
    const initialData: TokenHoverData = {
      token,
      type: token.toLowerCase().includes('curl(') ? 'function' : 'variable',
      loading: true
    };
    setHoverData(initialData);

    try {
      const authToken = authService.getAuthToken();
      const response = await fetch('/api/prompt/evaluate-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          token,
          variables,
          agentName
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const resultData: TokenHoverData = {
        token,
        type: data.type || (token.toLowerCase().includes('curl(') ? 'function' : 'variable'),
        name: data.name,
        argument: data.argument,
        targetUrl: data.targetUrl,
        value: data.value,
        source: data.source,
        description: data.description,
        status: data.status || 'success',
        loading: false
      };

      evalCache.current[token] = resultData;

      // Обновляем hoverData, если пользователь всё еще наведен на этот токен
      if (activeTokenRef.current === token || isOverTooltipRef.current) {
        setHoverData(resultData);
      }
    } catch (err: any) {
      const errorData: TokenHoverData = {
        token,
        type: token.toLowerCase().includes('curl(') ? 'function' : 'variable',
        value: `Ошибка XHR: ${err.message}`,
        status: 'error',
        loading: false
      };
      evalCache.current[token] = errorData;
      if (activeTokenRef.current === token || isOverTooltipRef.current) {
        setHoverData(errorData);
      }
    }
  }, [variables, agentName]);

  // Расчет умного позиционирования подсказки на экране
  const calculateSmartPosition = useCallback((rect: DOMRect) => {
    const tooltipWidth = 400;
    const tooltipHeight = 250;
    const padding = 16;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let placement: 'bottom' | 'top' = 'bottom';
    let top = rect.bottom + 8;

    if (spaceBelow < tooltipHeight && spaceAbove > spaceBelow) {
      placement = 'top';
      top = Math.max(padding, rect.top - tooltipHeight - 8);
    } else {
      top = Math.min(rect.bottom + 8, window.innerHeight - tooltipHeight - padding);
    }

    let left = rect.left;
    let align: 'left' | 'right' = 'left';

    if (left + tooltipWidth > window.innerWidth - padding) {
      align = 'right';
      left = Math.max(padding, window.innerWidth - tooltipWidth - padding);
    } else if (left < padding) {
      left = padding;
    }

    return { top, left, placement, align };
  }, []);

  // Открытие тултипа для элемента токена
  const openTooltipForElement = useCallback((targetEl: HTMLElement, token: string) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    activeTokenRef.current = token;
    const rect = targetEl.getBoundingClientRect();
    const pos = calculateSmartPosition(rect);

    setTooltipState({
      visible: true,
      top: pos.top,
      left: pos.left,
      placement: pos.placement,
      align: pos.align,
      token
    });

    fetchTokenEvaluation(token);
  }, [calculateSmartPosition, fetchTokenEvaluation]);

  // Обработка движения мыши над textarea редактора
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Временно отключаем pointer-events на textarea для проверки лежащего под ним элемента
    textarea.style.pointerEvents = 'none';
    const hoveredEl = document.elementFromPoint(e.clientX, e.clientY);
    textarea.style.pointerEvents = 'auto';

    const tokenEl = hoveredEl?.closest('[data-token]') as HTMLElement | null;

    if (tokenEl) {
      const token = tokenEl.getAttribute('data-token');
      if (token) {
        if (activeTokenRef.current !== token) {
          openTooltipForElement(tokenEl, token);
        } else if (leaveTimeoutRef.current) {
          clearTimeout(leaveTimeoutRef.current);
          leaveTimeoutRef.current = null;
        }
        return;
      }
    }

    // Если курсор не над токеном, запускаем плавное закрытие
    if (activeTokenRef.current && !leaveTimeoutRef.current && !isOverTooltipRef.current) {
      leaveTimeoutRef.current = setTimeout(() => {
        closeTooltip();
      }, 200);
    }
  }, [openTooltipForElement, closeTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (!isOverTooltipRef.current) {
      leaveTimeoutRef.current = setTimeout(() => {
        closeTooltip();
      }, 200);
    }
  }, [closeTooltip]);

  // Рендеринг IDE-подобных токенов подсветки синтаксиса
  const renderHighlightedContent = () => {
    const text = value || '';
    if (!text) {
      return <span className="text-muted">{placeholder}</span>;
    }

    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    MASTER_TOKEN_REGEX.lastIndex = 0;

    let keyCounter = 0;

    while ((match = MASTER_TOKEN_REGEX.exec(text)) !== null) {
      const matchIndex = match.index;
      const fullMatch = match[0];

      // Добавляем обычный текст перед токеном
      if (matchIndex > lastIndex) {
        elements.push(
          <span key={`text-${keyCounter++}`}>
            {text.substring(lastIndex, matchIndex)}
          </span>
        );
      }

      // 1. Функция curl(...)
      if (match[1]) {
        const fnMatch = fullMatch.match(/^(\{\{|\{)\s*(curl)\s*\(\s*([^)]+?)\s*\)\s*(\}\}|\})$/i);
        if (fnMatch) {
          const openBrace = fnMatch[1];
          const fnName = fnMatch[2];
          const rawArg = fnMatch[3].trim();
          const closeBrace = fnMatch[4];
          const isString = /^['"`].*['"`]$/.test(rawArg) || rawArg.startsWith('http://') || rawArg.startsWith('https://');

          elements.push(
            <span
              key={`func-${keyCounter++}`}
              className="token-expr token-func"
              data-token={fullMatch}
            >
              <span className="ide-brace">{openBrace}</span>
              <span className="ide-fn-name">{fnName}</span>
              <span className="ide-paren">(</span>
              <span className={isString ? "ide-string" : "ide-arg-var"}>{rawArg}</span>
              <span className="ide-paren">)</span>
              <span className="ide-brace">{closeBrace}</span>
            </span>
          );
        } else {
          elements.push(
            <span key={`func-raw-${keyCounter++}`} className="token-expr token-func" data-token={fullMatch}>
              {fullMatch}
            </span>
          );
        }
      } 
      // 2. Переменная {{...}}
      else if (match[2]) {
        const varMatch = fullMatch.match(/^(\{\{|\{)\s*([a-zA-Z0-9_-]+)\s*(\}\}|\})$/);
        if (varMatch) {
          const openBrace = varMatch[1];
          const varName = varMatch[2];
          const closeBrace = varMatch[3];

          elements.push(
            <span
              key={`var-${keyCounter++}`}
              className="token-expr token-var"
              data-token={fullMatch}
            >
              <span className="ide-brace">{openBrace}</span>
              <span className="ide-var-name">{varName}</span>
              <span className="ide-brace">{closeBrace}</span>
            </span>
          );
        } else {
          elements.push(
            <span key={`var-raw-${keyCounter++}`} className="token-expr token-var" data-token={fullMatch}>
              {fullMatch}
            </span>
          );
        }
      } 
      // 3. Секция [РАЗДЕЛ]
      else if (match[3]) {
        const secMatch = fullMatch.match(/^(\[)([^\]]+)(\])$/);
        if (secMatch) {
          elements.push(
            <span key={`sec-${keyCounter++}`} className="token-section">
              <span className="ide-section-bracket">{secMatch[1]}</span>
              <span className="ide-section-title">{secMatch[2]}</span>
              <span className="ide-section-bracket">{secMatch[3]}</span>
            </span>
          );
        } else {
          elements.push(
            <span key={`sec-raw-${keyCounter++}`} className="token-section">
              {fullMatch}
            </span>
          );
        }
      } 
      // 4. Шаг / Номер пункта
      else if (match[4]) {
        elements.push(
          <span key={`step-${keyCounter++}`} className="token-step">
            {fullMatch}
          </span>
        );
      }

      lastIndex = matchIndex + fullMatch.length;
    }

    // Добавляем оставшийся текст
    if (lastIndex < text.length) {
      elements.push(
        <span key={`text-end-${keyCounter++}`}>
          {text.substring(lastIndex)}
        </span>
      );
    }

    // Невидимый завершающий символ для правильного переноса строк в pre
    if (text.endsWith('\n')) {
      elements.push(<span key={`eol-${keyCounter++}`}> </span>);
    }

    return elements;
  };

  // Список всех доступных переменных для чипов быстрой вставки
  const allChips = Array.from(new Set([
    ...SUPPORTED_TEMPLATE_VARIABLES.map(v => `{{${v.key}}}`),
    ...Object.keys(variables).map(k => `{{${k}}}`),
    ...SUPPORTED_TEMPLATE_FUNCTIONS.map(f => f.syntax)
  ]));

  return (
    <div className="prompt-editor-component" ref={containerRef}>
      {/* Тулбар быстрой вставки прямо над редактором */}
      <div className="prompt-editor-toolbar">
        <span className="toolbar-label">ВСТАВИТЬ В ПОЗИЦИЮ КУРСОРА:</span>
        <div className="toolbar-chips">
          {allChips.map((chipText) => {
            const isFunc = chipText.includes('curl(');
            return (
              <button
                key={chipText}
                type="button"
                className={`prompt-chip-btn font-mono ${isFunc ? 'chip-func' : 'chip-var'}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => insertAtCursor(chipText)}
                onMouseEnter={(e) => {
                  openTooltipForElement(e.currentTarget, chipText);
                }}
                onMouseLeave={handleMouseLeave}
                title={isFunc ? 'Функция HTTP GET с кэшем' : `Вставить переменную ${chipText}`}
              >
                {chipText}
              </button>
            );
          })}
        </div>
      </div>

      {/* Контейнер редактора с двойным слоем (подсветка + textarea) */}
      <div
        className="prompt-editor-box"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Нижний слой: IDE подсветка синтаксиса токенов */}
        <pre
          ref={backdropRef}
          className="prompt-syntax-backdrop font-mono"
          style={{ fontFamily: 'monospace' }}
          aria-hidden="true"
        >
          {renderHighlightedContent()}
        </pre>

        {/* Верхний слой: прозрачный textarea для нативного ввода и каретки */}
        <textarea
          ref={textareaRef}
          className="prompt-syntax-textarea font-mono"
          style={{ fontFamily: 'monospace' }}
          value={value}
          rows={rows}
          placeholder={placeholder}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          onChange={(e) => {
            onChange(e.target.value);
            handleUpdateCursor();
          }}
          onScroll={syncScroll}
          onClick={handleUpdateCursor}
          onKeyUp={handleUpdateCursor}
          onSelect={handleUpdateCursor}
          onFocus={handleUpdateCursor}
        />
      </div>

      {/* Премиальная стеклянная подсказка (Hover Tooltip), рендерится через Portal в document.body */}
      {tooltipState.visible && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className={`prompt-token-tooltip placement-${tooltipState.placement} align-${tooltipState.align}`}
          style={{
            top: `${tooltipState.top}px`,
            left: `${tooltipState.left}px`
          }}
          onMouseEnter={() => {
            isOverTooltipRef.current = true;
            if (leaveTimeoutRef.current) {
              clearTimeout(leaveTimeoutRef.current);
              leaveTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            isOverTooltipRef.current = false;
            closeTooltip();
          }}
        >
          <div className="tooltip-glass-card">
            <div className="tooltip-header">
              <span className={`tooltip-badge badge-${hoverData?.type || (tooltipState.token.includes('curl(') ? 'function' : 'variable')}`}>
                {(hoverData?.type === 'function' || tooltipState.token.includes('curl(')) ? '⚡ FUNCTION' : '🏷️ VARIABLE'}
              </span>
              <span className="tooltip-token-name font-mono">{tooltipState.token}</span>
              {hoverData?.loading && <span className="tooltip-spinner">⏳</span>}
            </div>

            {hoverData?.targetUrl && (
              <div className="tooltip-row">
                <span className="tooltip-meta-label">TARGET URL:</span>
                <span className="tooltip-meta-value font-mono">{hoverData.targetUrl}</span>
              </div>
            )}

            {hoverData?.description && (
              <div className="tooltip-desc">{hoverData.description}</div>
            )}

            <div className="tooltip-preview-section">
              <div className="tooltip-preview-header">
                <span className="tooltip-preview-title">ЗНАЧЕНИЕ (XHR):</span>
                {hoverData?.value && hoverData.value.length > 80 && (
                  <span className="tooltip-char-count">{hoverData.value.length} символов</span>
                )}
              </div>

              <div className="tooltip-preview-container">
                {hoverData?.loading ? (
                  <div className="tooltip-loading-state">
                    <span className="loading-dot-pulse"></span>
                    <span>Запрос значения через XHR...</span>
                  </div>
                ) : (
                  <pre className="tooltip-preview-code font-mono">
                    {hoverData?.value !== undefined && hoverData?.value !== null && hoverData?.value !== ''
                      ? hoverData.value
                      : '(пустое значение или переменная не определена)'}
                  </pre>
                )}
              </div>
            </div>

            {hoverData?.source && (
              <div className="tooltip-footer">
                <span className="tooltip-source-tag">
                  <strong>Источник:</strong> {hoverData.source}
                </span>
                <span className="tooltip-status-indicator status-ready">✓ Готово</span>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});
